import * as Y from "yjs";
import type {
  Annotation,
  AnnotationTarget,
  AnnotationThread,
  FrameTarget,
  ThreadMessage,
  UserInfo,
} from "../types";
import { readAnnotations } from "./annotations";

function generateId(prefix: "thread" | "msg"): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function serializeThread(thread: AnnotationThread): Y.Map<unknown> {
  const yMap = new Y.Map<unknown>();
  yMap.set("id", thread.id);
  yMap.set("projectId", thread.projectId ?? null);
  yMap.set("reviewId", thread.reviewId ?? null);
  yMap.set("pageId", thread.pageId ?? null);
  yMap.set("status", thread.status);
  yMap.set("createdAt", thread.createdAt);
  yMap.set("updatedAt", thread.updatedAt);
  yMap.set("author", thread.author);
  yMap.set("target", JSON.stringify(thread.target));

  const messages = new Y.Array<Y.Map<unknown>>();
  thread.messages.forEach((message) => {
    messages.push([serializeMessage(message)]);
  });
  yMap.set("messages", messages);

  return yMap;
}

function serializeMessage(message: ThreadMessage): Y.Map<unknown> {
  const yMap = new Y.Map<unknown>();
  yMap.set("id", message.id);
  yMap.set("text", message.text);
  yMap.set("author", message.author);
  yMap.set("createdAt", message.createdAt);
  if (message.editedAt) yMap.set("editedAt", message.editedAt);
  return yMap;
}

function parseMessage(yMap: Y.Map<unknown>): ThreadMessage {
  return {
    id: (yMap.get("id") as string) ?? generateId("msg"),
    text: (yMap.get("text") as string) ?? "",
    author: (yMap.get("author") as UserInfo | undefined) ?? {
      name: "Anonymous",
      color: "#94a3b8",
    },
    createdAt: (yMap.get("createdAt") as string) ?? new Date().toISOString(),
    editedAt: (yMap.get("editedAt") as string | undefined) ?? undefined,
    deletedAt: (yMap.get("deletedAt") as string | undefined) ?? undefined,
    deletedBy: (yMap.get("deletedBy") as string | undefined) ?? undefined,
  };
}

function parseTarget(rawTarget: unknown): AnnotationTarget | null {
  if (typeof rawTarget !== "string") {
    return {
      kind: "frame",
      frameId: "unknown",
      x: 0.5,
      y: 0.5,
    } satisfies FrameTarget;
  }

  try {
    return JSON.parse(rawTarget) as AnnotationTarget;
  } catch {
    // Malformed target written by a remote peer; skip this entry.
    return null;
  }
}

function parseThread(yMap: Y.Map<unknown>): AnnotationThread | null {
  const target = parseTarget(yMap.get("target"));
  if (!target) return null;
  const messagesArray =
    (yMap.get("messages") as Y.Array<Y.Map<unknown>> | undefined) ??
    new Y.Array<Y.Map<unknown>>();
  const messages = messagesArray.toArray().map(parseMessage);
  const author = (yMap.get("author") as UserInfo | undefined) ??
    messages[0]?.author ?? { name: "Anonymous", color: "#94a3b8" };
  const createdAt =
    (yMap.get("createdAt") as string | undefined) ??
    messages[0]?.createdAt ??
    new Date().toISOString();

  return {
    id: (yMap.get("id") as string) ?? generateId("thread"),
    projectId: (yMap.get("projectId") as string | null) ?? undefined,
    reviewId: (yMap.get("reviewId") as string | null) ?? undefined,
    pageId: (yMap.get("pageId") as string | null) ?? undefined,
    status:
      (yMap.get("status") as "open" | "managed" | "resolved" | undefined) ??
      "open",
    createdAt,
    updatedAt: (yMap.get("updatedAt") as string | undefined) ?? createdAt,
    author,
    messages,
    target,
    deletedAt: (yMap.get("deletedAt") as string | undefined) ?? undefined,
    deletedBy: (yMap.get("deletedBy") as string | undefined) ?? undefined,
  };
}

/**
 * Drop soft-deleted threads and messages from the read model so the widget
 * hides comments an operator removed from the dashboard. A thread with no
 * surviving messages is treated as deleted.
 */
function withoutDeleted(thread: AnnotationThread): AnnotationThread | null {
  if (thread.deletedAt) return null;
  const messages = thread.messages.filter((message) => !message.deletedAt);
  // Drop a thread only if deletion emptied it — keep genuinely message-less
  // (legacy) threads untouched.
  if (messages.length === 0 && thread.messages.length > 0) return null;
  return { ...thread, messages };
}

export function getThreadsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap("threads") as Y.Map<Y.Map<unknown>>;
}

export function annotationToThread(annotation: Annotation): AnnotationThread {
  return {
    id: annotation.id,
    status: annotation.resolved ? "resolved" : "open",
    createdAt: annotation.createdAt,
    updatedAt: annotation.createdAt,
    author: annotation.author,
    target: {
      kind: "frame",
      frameId: annotation.frameId,
      x: annotation.x,
      y: annotation.y,
    },
    messages: [
      {
        id: `${annotation.id}_root`,
        text: annotation.text,
        author: annotation.author,
        createdAt: annotation.createdAt,
      },
      ...annotation.replies.map((reply) => ({
        id: reply.id,
        text: reply.text,
        author: reply.author,
        createdAt: reply.createdAt,
      })),
    ],
  };
}

export function threadToAnnotation(
  thread: AnnotationThread,
): Annotation | null {
  if (thread.target.kind !== "frame") return null;

  const [root, ...replies] = thread.messages;
  return {
    id: thread.id,
    frameId: thread.target.frameId,
    x: thread.target.x,
    y: thread.target.y,
    text: root?.text ?? "",
    author: root?.author ?? thread.author,
    createdAt: root?.createdAt ?? thread.createdAt,
    resolved: thread.status === "resolved",
    replies: replies.map((message) => ({
      id: message.id,
      text: message.text,
      author: message.author,
      createdAt: message.createdAt,
    })),
  };
}

export function readThreads(doc: Y.Doc): AnnotationThread[] {
  const threadsMap = getThreadsMap(doc);
  const nextThreads = new Map<string, AnnotationThread>();

  threadsMap.forEach((value) => {
    const parsed = parseThread(value);
    const thread = parsed ? withoutDeleted(parsed) : null;
    if (thread) nextThreads.set(thread.id, thread);
  });

  for (const annotation of readAnnotations(doc)) {
    if (!nextThreads.has(annotation.id)) {
      nextThreads.set(annotation.id, annotationToThread(annotation));
    }
  }

  return [...nextThreads.values()].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

export function addThread(
  doc: Y.Doc,
  target: AnnotationTarget,
  text: string,
  author: UserInfo,
  context?: {
    projectId?: string;
    reviewId?: string;
    pageId?: string;
  },
): AnnotationThread {
  const thread = createThreadRecord(target, text, author, context);
  getThreadsMap(doc).set(thread.id, serializeThread(thread));
  return thread;
}

export function createThreadRecord(
  target: AnnotationTarget,
  text: string,
  author: UserInfo,
  context?: {
    projectId?: string;
    reviewId?: string;
    pageId?: string;
  },
): AnnotationThread {
  const now = new Date().toISOString();
  return {
    id: generateId("thread"),
    projectId: context?.projectId,
    reviewId: context?.reviewId,
    pageId: context?.pageId,
    status: "open",
    createdAt: now,
    updatedAt: now,
    author,
    target,
    messages: [
      {
        id: generateId("msg"),
        text,
        author,
        createdAt: now,
      },
    ],
  };
}

export function addFrameThread(
  doc: Y.Doc,
  frameId: string,
  position: { x: number; y: number },
  text: string,
  author: UserInfo,
  context?: {
    projectId?: string;
    reviewId?: string;
    pageId?: string;
    url?: string;
  },
): AnnotationThread {
  return addThread(
    doc,
    {
      kind: "frame",
      frameId,
      x: position.x,
      y: position.y,
      url: context?.url,
    },
    text,
    author,
    context,
  );
}

export function addThreadReply(
  doc: Y.Doc,
  threadId: string,
  text: string,
  author: UserInfo,
): ThreadMessage | null {
  const threadsMap = getThreadsMap(doc);
  const existing = threadsMap.get(threadId);
  if (!existing) return null;

  const message: ThreadMessage = {
    id: generateId("msg"),
    text,
    author,
    createdAt: new Date().toISOString(),
  };
  const messages = existing.get("messages") as Y.Array<Y.Map<unknown>>;
  messages.push([serializeMessage(message)]);
  existing.set("updatedAt", message.createdAt);
  return message;
}

export function setThreadResolved(
  doc: Y.Doc,
  threadId: string,
  resolved: boolean,
): void {
  setThreadStatus(doc, threadId, resolved ? "resolved" : "open");
}

export function setThreadStatus(
  doc: Y.Doc,
  threadId: string,
  status: "open" | "managed" | "resolved",
): void {
  const threadsMap = getThreadsMap(doc);
  const existing = threadsMap.get(threadId);
  if (!existing) return;
  existing.set("status", status);
  existing.set("updatedAt", new Date().toISOString());
}

export function deleteThread(doc: Y.Doc, threadId: string): void {
  getThreadsMap(doc).delete(threadId);
}

export function editThreadMessage(
  doc: Y.Doc,
  threadId: string,
  messageId: string,
  text: string,
): boolean {
  const threadsMap = getThreadsMap(doc);
  const existing = threadsMap.get(threadId);
  if (!existing) return false;

  const messages = existing.get("messages") as Y.Array<Y.Map<unknown>>;
  const editedAt = new Date().toISOString();
  let edited = false;

  doc.transact(() => {
    for (let index = 0; index < messages.length; index += 1) {
      const message = messages.get(index);
      if (message.get("id") !== messageId) continue;
      message.set("text", text);
      message.set("editedAt", editedAt);
      existing.set("updatedAt", editedAt);
      edited = true;
      break;
    }
  });

  return edited;
}

export function deleteThreadMessage(
  doc: Y.Doc,
  threadId: string,
  messageId: string,
): boolean {
  const threadsMap = getThreadsMap(doc);
  const existing = threadsMap.get(threadId);
  if (!existing) return false;

  const messages = existing.get("messages") as Y.Array<Y.Map<unknown>>;
  let deleted = false;

  doc.transact(() => {
    for (let index = 0; index < messages.length; index += 1) {
      const message = messages.get(index);
      if (message.get("id") !== messageId) continue;
      messages.delete(index, 1);
      deleted = true;
      break;
    }
    if (deleted) {
      if (messages.length === 0) {
        threadsMap.delete(threadId);
      } else {
        existing.set("updatedAt", new Date().toISOString());
      }
    }
  });

  return deleted;
}

export function observeThreads(
  doc: Y.Doc,
  handler: (threads: AnnotationThread[]) => void,
): () => void {
  const threadsMap = getThreadsMap(doc);
  const onUpdate = () => handler(readThreads(doc));
  threadsMap.observeDeep(onUpdate);
  return () => {
    threadsMap.unobserveDeep(onUpdate);
  };
}
